
import { useEffect, useState, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useDebounce } from "@/hooks/use-debounce";

interface AddressAutocompleteProps {
  onAddressSelect: (address: {
    address: string;
    city: string;
    state: string;
    zipCode: string;
  }) => void;
  onInputChange?: (value: string) => void;
  initialAddress?: string;
  className?: string;
  showLabel?: boolean;
  label?: string;
}

interface Prediction {
  description: string;
  place_id: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

export const AddressAutocomplete = ({
  onAddressSelect,
  onInputChange,
  initialAddress = "",
  className = "",
  showLabel = true,
  label = "Street Address*"
}: AddressAutocompleteProps) => {
  const [inputValue, setInputValue] = useState(initialAddress);
  const [predictions, setPredictions] = useState<Prediction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const [justSelected, setJustSelected] = useState(false);
  const [userDismissed, setUserDismissed] = useState(false);
  const [hasFocused, setHasFocused] = useState(false);
  
  const debouncedInput = useDebounce(inputValue, 300);

  // Sync with initialAddress prop when it changes (e.g., when form data loads asynchronously)
  useEffect(() => {
    setInputValue(initialAddress);
  }, [initialAddress]);

  const searchPlaces = useCallback(async (input: string) => {
    if (isSelecting) return;
    
    if (justSelected) {
      setJustSelected(false);
      setPredictions([]);
      setShowDropdown(false);
      return;
    }
    
    if (input.length < 3) {
      setPredictions([]);
      setShowDropdown(false);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('google-places-autocomplete', {
        body: { input }
      });

      if (error) {
        console.error('Places search error:', error);
        setPredictions([]);
      } else {
        setPredictions(data?.predictions || []);
        if (hasFocused) {
          setShowDropdown(true);
        }
      }
    } catch (error) {
      console.error('Places search error:', error);
      setPredictions([]);
    } finally {
      setIsLoading(false);
    }
  }, [isSelecting, justSelected, hasFocused]);

  const handleDone = () => {
    setShowDropdown(false);
    setPredictions([]);
    setUserDismissed(true);
  };

  const handlePlaceSelect = useCallback(async (prediction: Prediction) => {
    setIsSelecting(true);
    setJustSelected(true);
    setShowDropdown(false);
    setPredictions([]);
    setUserDismissed(true);
    setIsLoading(true);

    try {
      console.log('Fetching place details for:', prediction.place_id);
      
      const { data, error } = await supabase.functions.invoke('google-places-autocomplete', {
        body: { place_id: prediction.place_id }
      });

      if (error) {
        console.error('Place details error:', error);
        setInputValue(prediction.structured_formatting.main_text);
        return;
      }

      if (data) {
        console.log('Place details response:', data);
        
        // Set only the street address in the input field, not the full description
        const streetAddress = data.address || prediction.structured_formatting.main_text;
        setInputValue(streetAddress);
        
        onAddressSelect({
          address: streetAddress,
          city: data.city || '',
          state: data.state || '',
          zipCode: data.zipCode || ''
        });
      }
    } catch (error) {
      console.error('Place details error:', error);
      // Fallback to just the main text from prediction
      setInputValue(prediction.structured_formatting.main_text);
    } finally {
      setIsLoading(false);
      setIsSelecting(false);
    }
  }, [onAddressSelect]);

  useEffect(() => {
    searchPlaces(debouncedInput);
  }, [debouncedInput, searchPlaces]);

  return (
    <div className={`relative space-y-2 ${className}`}>
      {showLabel && <Label htmlFor="address-autocomplete">{label}</Label>}
      <Input
        id="address-autocomplete"
        type="text"
        placeholder="Type to search for an address"
        value={inputValue}
        onChange={(e) => {
          const newValue = e.target.value;
          setInputValue(newValue);
          setJustSelected(false);
          setUserDismissed(false);
          onInputChange?.(newValue);
          if (newValue.length < 3) {
            setShowDropdown(false);
          }
        }}
        onFocus={() => {
          setHasFocused(true);
          if (!justSelected && !userDismissed && !isSelecting && predictions.length > 0) {
            setShowDropdown(true);
          }
        }}
        onBlur={() => {
          setTimeout(() => {
            if (!isSelecting) {
              setShowDropdown(false);
            }
          }, 300);
        }}
        className="bg-muted"
        required
      />
      
      {showDropdown && predictions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-background border border-border rounded-md shadow-lg overflow-hidden">
          <div className="max-h-48 overflow-y-auto">
            {predictions.map((prediction) => (
              <div
                key={prediction.place_id}
                className="px-3 py-2 cursor-pointer hover:bg-muted border-b border-border last:border-b-0"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handlePlaceSelect(prediction);
                }}
              >
                <div className="font-medium text-sm">
                  {prediction.structured_formatting.main_text}
                </div>
                <div className="text-xs text-muted-foreground">
                  {prediction.structured_formatting.secondary_text}
                </div>
              </div>
            ))}
          </div>
          
          <div className="border-t border-border bg-muted/50 p-2">
            <button
              type="button"
              onClick={handleDone}
              onMouseDown={(e) => e.preventDefault()}
              className="w-full px-3 py-2 text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 rounded-md transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      )}
      
      {isLoading && (
        <p className="text-sm text-muted-foreground mt-1">Searching addresses...</p>
      )}
    </div>
  );
};
